import { useLayoutEffect, useRef } from 'react';
import { preload } from 'react-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { heroPairs } from '@/mocks/site';
import { setHeroActive } from '@/hooks/heroState';

gsap.registerPlugin(ScrollTrigger);

const LEFT_HIDDEN = 'inset(100% 0% 0% 0%)';
const RIGHT_HIDDEN = 'inset(0% 0% 100% 0%)';
const SHOWN = 'inset(0% 0% 0% 0%)';

const TITLE = 'The Art of Surfaces.';
const titleClass = 'font-heading font-semibold uppercase tracking-[-0.02em] leading-[0.98] text-[clamp(2.4rem,7.4vw,7rem)] max-w-[13ch] [grid-area:1/1]';
// Material treatment for scenes 02-05 (scene 01 = solid H1).
const MATERIALS = ['hero-title-limestone', 'hero-title-clay', 'hero-title-mineral', 'hero-title-sienna'];

const TRANSITION = 1;
const HOLD = 0.35;

export default function SplitHero() {
  const root = useRef<HTMLElement>(null);

  // Scene 01 = high priority (LCP); scene 02 preloaded at normal priority for the first scroll.
  // Scenes 03-05 are requested by their <img> at low priority, queued behind 01/02.
  heroPairs.slice(0, 2).forEach((p, i) => {
    const fetchPriority = i === 0 ? 'high' : 'auto';
    preload(p.left.src, { as: 'image', fetchPriority });
    preload(p.right.src, { as: 'image', fetchPriority });
  });

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    let disposed = false;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
        const lefts = gsap.utils.toArray<HTMLElement>('[data-hero-left] [data-layer]', el);
        const rights = gsap.utils.toArray<HTMLElement>('[data-hero-right] [data-layer]', el);
        const titles = [el.querySelector('[data-hero-title]'), ...gsap.utils.toArray<HTMLElement>('[data-title-mat]', el)];

        // Establish every initial state up front, before any interaction.
        gsap.set(lefts.slice(1), { clipPath: LEFT_HIDDEN });
        gsap.set(rights.slice(1), { clipPath: RIGHT_HIDDEN });

        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: el,
            start: 'top top',
            end: '+=500%',
            pin: true,
            pinSpacing: true,
            scrub: 1,
            invalidateOnRefresh: true,
            onToggle: (self) => setHeroActive(self.isActive),
            onRefresh: (self) => setHeroActive(self.isActive),
          },
        });

        tl.to({}, { duration: 0.3 });
        for (let i = 1; i < heroPairs.length; i += 1) {
          const at = tl.duration();
          const ease = 'power2.inOut';
          tl.fromTo(lefts[i], { clipPath: LEFT_HIDDEN }, { clipPath: SHOWN, duration: TRANSITION, ease, immediateRender: false }, at)
            .fromTo(rights[i], { clipPath: RIGHT_HIDDEN }, { clipPath: SHOWN, duration: TRANSITION, ease, immediateRender: false }, at)
            .fromTo(lefts[i].firstElementChild, { yPercent: 6 }, { yPercent: 0, duration: TRANSITION, ease }, at)
            .fromTo(rights[i].firstElementChild, { yPercent: -6 }, { yPercent: 0, duration: TRANSITION, ease }, at)
            .fromTo(titles[i], { opacity: 0 }, { opacity: 1, duration: 0.6 }, at + 0.25)
            .to(titles[i - 1], { opacity: 0, duration: 0.6 }, at + 0.25)
            .to({}, { duration: i === heroPairs.length - 1 ? 0.55 : HOLD });
        }

        return () => setHeroActive(false);
      });
    }, el);

    // Decode every scene image before it can be revealed; then refresh once.
    const imgs = Array.from(el.querySelectorAll<HTMLImageElement>('img[data-hero-img]'));
    Promise.allSettled(imgs.map((img) => (img.complete ? img.decode() : new Promise<void>((res) => {
      img.addEventListener('load', () => { img.decode().then(res, res); }, { once: true });
      img.addEventListener('error', () => res(), { once: true });
    })))).then(() => {
      if (!disposed) ScrollTrigger.refresh();
    });
    document.fonts?.ready.then(() => { if (!disposed) ScrollTrigger.refresh(); }).catch(() => undefined);

    return () => {
      disposed = true;
      ctx.revert();
      setHeroActive(false);
    };
  }, []);

  const renderStack = (side: 'left' | 'right') => heroPairs.map((p, i) => {
    const img = side === 'left' ? p.left : p.right;
    const hidden = side === 'left' ? LEFT_HIDDEN : RIGHT_HIDDEN;
    return (
      <div
        key={p.id}
        data-layer
        data-pair={p.id}
        className="absolute inset-0 overflow-hidden [will-change:clip-path]"
        style={{ clipPath: i === 0 ? SHOWN : hidden, zIndex: i + 1 }}
      >
        <img
          data-hero-img
          src={img.src}
          alt={i === 0 ? img.alt : ''}
          aria-hidden={i !== 0}
          width={1200}
          height={1400}
          loading="eager"
          decoding={i === 0 ? 'sync' : 'async'}
          fetchPriority={i === 0 ? 'high' : i === 1 ? 'auto' : 'low'}
          // A failed layer becomes transparent, so the previous scene stays visible.
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          className={`absolute inset-0 w-full h-full object-cover object-center ${i === 0 ? '' : 'scale-[1.08]'}`}
        />
      </div>
    );
  });

  return (
    <section ref={root} className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-background-900" aria-label="Introduction">
      <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
        <div data-hero-left className="relative h-full overflow-hidden">{renderStack('left')}</div>
        <div data-hero-right className="relative h-full overflow-hidden hidden md:block">{renderStack('right')}</div>
      </div>
      {/* Soft legibility veil: very wide, multi-stop falloff so no visible edge. */}
      <div className="absolute inset-0 z-[8] pointer-events-none bg-[radial-gradient(ellipse_75%_65%_at_50%_52%,oklch(var(--primary-900)/0.34)_0%,oklch(var(--primary-900)/0.24)_30%,oklch(var(--primary-900)/0.12)_58%,oklch(var(--primary-900)/0.04)_80%,transparent_100%)]" />
      <div className="absolute inset-x-0 top-0 z-[8] h-40 pointer-events-none bg-[linear-gradient(to_bottom,oklch(var(--primary-900)/0.32)_0%,oklch(var(--primary-900)/0.14)_45%,transparent_100%)]" />
      <div className="absolute inset-x-0 bottom-0 z-[8] h-40 pointer-events-none bg-[linear-gradient(to_top,oklch(var(--primary-900)/0.22)_0%,oklch(var(--primary-900)/0.08)_50%,transparent_100%)]" />

      <div data-hero-overlay className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-4 text-background-50">
        <p className="text-[11px] md:text-xs uppercase tracking-[0.3em] mb-5">MATERA / Exterior Surface Works</p>
        <div className="grid justify-items-center">
          <h1 data-hero-title className={titleClass}>{TITLE}</h1>
          {MATERIALS.map((m) => (
            <span key={m} aria-hidden="true" data-title-mat className={`${titleClass} ${m} opacity-0 pointer-events-none`}>{TITLE}</span>
          ))}
        </div>
        <a href="#philosophy" className="pointer-events-auto absolute bottom-8 flex flex-col items-center gap-2 text-[11px] uppercase tracking-[0.3em] cursor-pointer">
          Scroll to explore
          <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-down-line" /></span>
        </a>
      </div>
    </section>
  );
}